import { computed } from 'vue'

import type { JobListItem } from '@/composables/queue/useJobList'
import type { MenuEntry } from '@/composables/queue/useJobMenu'
import { st } from '@/i18n'
import { useNordyQueueStore } from '@/nordy/store/nordyQueueStore'
import type { NordyQueue } from '@/nordy/store/nordyQueueStore'
import { nordyApi } from '@/nordy/api'

/**
 * Nordy-specific version of useJobMenu.
 * Uses nordyQueueStore and nordyApi instead of upstream queueStore/api.
 */
export function useNordyJobMenu(
  currentMenuItem: () => JobListItem | null = () => null,
  onInspectAsset?: (item: JobListItem) => void
) {
  const queueStore = useNordyQueueStore()

  const resolveItem = (item?: JobListItem | null): JobListItem | null =>
    item ?? currentMenuItem()

  const resolveNordyTask = (
    item?: JobListItem | null
  ): NordyQueue | undefined => {
    const target = resolveItem(item)
    return target?.taskRef as unknown as NordyQueue | undefined
  }

  const cancelJob = async (item?: JobListItem | null) => {
    const task = resolveNordyTask(item)
    if (!task) return

    if (task.isRunning && task.jobId) {
      await nordyApi.cancelJob(task.jobId)
    } else if ((task.isPending || task.isQueued) && task.jobId) {
      await nordyApi.cancelJob(task.jobId)
    }

    queueStore.deleteTask(task.id)
  }

  const removeFailedJob = async (item?: JobListItem | null) => {
    const task = resolveNordyTask(item)
    if (!task) return
    queueStore.deleteTask(task.id)
  }

  const jobMenuCancelLabel = computed(() =>
    st('queue.jobMenu.cancelJob', 'Cancel job')
  )

  const jobMenuEntries = computed<MenuEntry[]>(() => {
    const item = currentMenuItem()
    const state = item?.state
    if (!state) return []

    const hasPreviewAsset = !!resolveNordyTask(item)?.previewOutput

    if (state === 'completed') {
      return [
        {
          key: 'inspect-asset',
          label: st('queue.jobMenu.inspectAsset', 'Inspect asset'),
          icon: 'icon-[lucide--zoom-in]',
          disabled: !hasPreviewAsset || !onInspectAsset,
          onClick: onInspectAsset
            ? () => {
                const item = currentMenuItem()
                if (item) onInspectAsset(item)
              }
            : undefined
        },
        { kind: 'divider', key: 'd1' },
        {
          key: 'delete',
          label: st('queue.jobMenu.removeJob', 'Remove job'),
          icon: 'icon-[lucide--circle-minus]',
          onClick: () => removeFailedJob()
        }
      ]
    }
    if (state === 'failed') {
      return [
        {
          key: 'delete',
          label: st('queue.jobMenu.removeJob', 'Remove job'),
          icon: 'icon-[lucide--circle-minus]',
          onClick: () => removeFailedJob()
        }
      ]
    }
    // pending / running
    const task = resolveNordyTask(item) //!nordy - cancel 가능 여부 판단을 위해 태스크 조회
    //!nordy - Queued/Running 상태(백엔드 Job 생성됨)에서는 cancel 메뉴 비활성화
    const canCancel = task ? !task.isQueued && !task.isRunning : true
    return [
      {
        key: 'cancel-job',
        label: jobMenuCancelLabel.value,
        icon: 'icon-[lucide--x]',
        disabled: !canCancel, //!nordy - cancel 불가 상태 표시
        onClick: canCancel ? () => cancelJob() : undefined //!nordy - cancel 불가 시 핸들러 제거
      }
    ]
  })

  return {
    jobMenuEntries,
    cancelJob,
    removeFailedJob
  }
}
